import { defineStore } from "pinia"
import { ref, watch, computed } from "vue"
import { EmissionFactors, EmissionFactorClient } from "../../entities/NSwagGenerated"

export const useReportsStore = defineStore("reportsStore", () => {
	const storedValue = localStorage.getItem("selectedFactors")
	const storedSingleValue = localStorage.getItem("selectedFactorSingle")
	let selectedEmissionFactorName = ref<string | null>(storedSingleValue ? JSON.parse(storedSingleValue) : null)

	let selectedEmissionFactorNames = ref<string[]>(storedValue ? JSON.parse(storedValue) : [])
	let emissionFactors = ref<EmissionFactors[]>([])

	const getVisibleEmissionFactors = (useWeighted = false) => {
		return emissionFactors.value.filter((ef: EmissionFactors) => !(useWeighted && ef.excludeFromWeighted))
	}

	const getSelectedEmissionFactors = (useWeighted = false) => {
		return getVisibleEmissionFactors(useWeighted).filter((ef: EmissionFactors) => selectedEmissionFactorNames.value.some((s) => ef.emissionFactorName === s))
	}

	const pruneSelectedEmissionFactorNames = (useWeighted = false) => {
		const visibleNames = new Set(getVisibleEmissionFactors(useWeighted).map((ef) => ef.emissionFactorName).filter(Boolean))
		selectedEmissionFactorNames.value = selectedEmissionFactorNames.value.filter((name) => visibleNames.has(name))
	}

	// Computed property for active emission factors
	const selectedEmissionFactors = computed(() => getSelectedEmissionFactors(false))

	//computed property for single selected emission factor
	const selectedEmissionFactor = computed(() => {
		return emissionFactors.value.find((ef: EmissionFactors) => ef.emissionFactorName === selectedEmissionFactorName.value) || null
	})

	watch(selectedEmissionFactorNames, (newFactors) => {
		localStorage.setItem("selectedFactors", JSON.stringify(newFactors))
	})

	watch(selectedEmissionFactorName, (newValue) => {
		if (newValue) {
			localStorage.setItem("selectedFactorSingle", JSON.stringify(newValue))
		} else {
			localStorage.removeItem("selectedFactorSingle")
		}
	})

	// Load emission factors immediately when store is created
	const loadEmissionFactors = async () => {
		try {
			const emissionFactorClient = new EmissionFactorClient(import.meta.env.VITE_API_URL?.toString())
			const loadedFactors = await emissionFactorClient.list()

			// Group by emissionFactorName, then flatten with isSelectedByDefault logic
			// Filter for active factors that are selectable for results
			const grouped = loadedFactors
				.filter((factor) => !factor.inactive && factor.selectableForResults)
				.reduce(
					(groups, factor) => {
						const key = factor.emissionFactorName!
						if (!groups[key]) {
							groups[key] = []
						}
						groups[key].push(factor)
						return groups
					},
					{} as Record<string, EmissionFactors[]>
				)

			// Map each group to a single entry, merging isSelectedByDefault with OR logic
			emissionFactors.value = Object.values(grouped)
				.map((group) => {
					var factorsSelectedByDefault = group.filter((f) => f.isSelectedByDefault)
					if (factorsSelectedByDefault && factorsSelectedByDefault.length) return factorsSelectedByDefault[0]
					else return group[0]
				})
				.filter((ef) => ef && ef.id && ef.emissionFactorName) // Only include emission factors with valid ID and name
				.sort((a, b) => {
					const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER
					const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER
					if (orderA !== orderB) return orderA - orderB
					return (a.emissionFactorName || "").localeCompare(b.emissionFactorName || "")
				})
		} catch (error) {
			console.error("Failed to load emission factors:", error)
		}
	}

	const syncSelectionsWithAvailableEmissionFactors = () => {
		const validNames = emissionFactors.value
			.filter((ef) => ef.emissionFactorName && ef.id) // Only include factors with both name and ID
			.map((ef) => ef.emissionFactorName!)

		// Filter out any stale names from localStorage that don't match current emission factors
		if (selectedEmissionFactorNames.value.length > 0) {
			const filteredNames = selectedEmissionFactorNames.value.filter((name) => validNames.includes(name))

			// If we filtered out any stale names, update both the ref and localStorage
			if (filteredNames.length !== selectedEmissionFactorNames.value.length) {
				const removedNames = selectedEmissionFactorNames.value.filter((name) => !validNames.includes(name))
				console.warn("Removed invalid emission factors from localStorage:", removedNames)
				selectedEmissionFactorNames.value = filteredNames
				localStorage.setItem("selectedFactors", JSON.stringify(filteredNames))
			}
		}

		// Validate that all loaded emission factors have valid IDs
		const factorsWithoutIds = emissionFactors.value.filter((ef) => !ef.id)
		if (factorsWithoutIds.length > 0) {
			console.error("Found emission factors without IDs:", factorsWithoutIds)
		}

		// Only set defaults if no factors were stored in localStorage or all were filtered out
		if (!storedValue || selectedEmissionFactorNames.value.length === 0) {
			selectedEmissionFactorNames.value = emissionFactors.value.filter((ef) => ef.isSelectedByDefault).map((ef) => ef.emissionFactorName!)
		}

		if (selectedEmissionFactorName.value && !validNames.includes(selectedEmissionFactorName.value)) {
			selectedEmissionFactorName.value = null
		}

		if (!selectedEmissionFactorName.value && emissionFactors.value.length > 0) {
			const defaultFactor = emissionFactors.value.find((ef) => ef.isSelectedByDefault)
			selectedEmissionFactorName.value = defaultFactor?.emissionFactorName || emissionFactors.value[0].emissionFactorName!
		}
	}

	// Load immediately
	loadEmissionFactors().then(() => {
		syncSelectionsWithAvailableEmissionFactors()
	})

	return {
		selectedEmissionFactorNames,
		selectedEmissionFactorName,
		emissionFactors,
		selectedEmissionFactors,
		selectedEmissionFactor,
		getVisibleEmissionFactors,
		getSelectedEmissionFactors,
		pruneSelectedEmissionFactorNames,
		loadEmissionFactors,
		syncSelectionsWithAvailableEmissionFactors,
	}
})
